import { useOutletContext } from "react-router-dom";
import { Target, Eye, Heart, Users, Trophy, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const COURT_IMAGE = "https://images.pexels.com/photos/7648071/pexels-photo-7648071.jpeg";

const values = [
  {
    icon: Heart,
    title: "Passie",
    description: "Wij leven voor futsal. Elke training, elke wedstrijd, elke dag."
  },
  {
    icon: Users,
    title: "Teamwork",
    description: "Samen staan we sterker. Van de jongste jeugd tot het eerste elftal."
  },
  {
    icon: Trophy,
    title: "Excellentie",
    description: "We streven naar het hoogste niveau in alles wat we doen."
  },
  {
    icon: Star,
    title: "Respect",
    description: "Respect voor tegenstanders, scheidsrechters, en elkaar."
  }
];

export default function MissieVisie() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  return (
    <div className="min-h-screen" data-testid="missie-visie-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${COURT_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div>
            <h1 className="text-4xl md:text-5xl font-black text-white font-['Chivo']">MISSIE & VISIE</h1>
            <p className="text-[hsl(48,96%,53%)] font-medium mt-2">Wat drijft ons</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Mission */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className={`${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <Target className={`h-8 w-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <CardTitle className={`font-['Chivo'] text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Onze Missie
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className={`space-y-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              <p className="text-lg leading-relaxed">
                Futsal Gent streeft ernaar om de toonaangevende futsalclub in Oost-Vlaanderen te zijn, 
                waar spelers van alle niveaus hun potentieel kunnen ontwikkelen in een professionele 
                en inspirerende omgeving.
              </p>
              <p>
                Wij geloven dat futsal meer is dan een sport - het is een manier van leven die 
                discipline, teamwork en doorzettingsvermogen bijbrengt.
              </p>
            </CardContent>
          </Card>

          <Card className={`${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                  <Eye className={`h-8 w-8 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                </div>
                <CardTitle className={`font-['Chivo'] text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Onze Visie
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className={`space-y-4 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              <p className="text-lg leading-relaxed">
                Tegen 2030 willen wij erkend worden als de beste jeugdopleiding in België en 
                consistent presteren op het hoogste niveau van het Belgische futsal.
              </p>
              <p>
                Wij zien een toekomst waarin Futsal Gent niet alleen kampioenschappen wint, 
                maar ook een positieve impact heeft op de gemeenschap door sport.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="mb-12">
          <h2 className={`text-3xl font-black font-['Chivo'] mb-8 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Onze Waarden
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value) => {
              const Icon = value.icon;
              return (
                <Card key={value.title} className={`text-center ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
                  <CardContent className="pt-8 pb-6">
                    <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-[hsl(217,91%,60%)]/20' : 'bg-blue-100'}`}>
                      <Icon className={`h-8 w-8 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
                    </div>
                    <h3 className={`font-bold text-lg mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{value.title}</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Goals */}
        <Card className={`${isDark ? 'bg-gradient-to-r from-blue-900/50 to-slate-900/50 border-white/10' : 'bg-gradient-to-r from-blue-50 to-slate-50 border-slate-200 shadow-md'}`}>
          <CardHeader>
            <CardTitle className={`font-['Chivo'] text-2xl text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Strategische Doelen 2024-2028
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className={`text-4xl font-black font-['Chivo'] mb-2 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`}>50+</div>
                <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Jeugdspelers in opleiding</p>
              </div>
              <div className="text-center">
                <div className={`text-4xl font-black font-['Chivo'] mb-2 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`}>Top 3</div>
                <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Positie in Belgische competitie</p>
              </div>
              <div className="text-center">
                <div className={`text-4xl font-black font-['Chivo'] mb-2 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`}>100%</div>
                <p className={`${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Inzet voor onze gemeenschap</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
